import React, { useEffect, useMemo } from 'react';
import { css as cssStyle } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from '@gumtree/shared/src/util/object-helpers';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';

import MultiSelectGroup, { MultiSelectGroupProps } from './multi-select-group';
import { SrpState, SrpUseDispatch } from '../../reducers';
// import { setFiltersBulk } from '../../reducers/current-filter-states';
import { FilterContainer, ClearAll, TagList, StyledTag, FilterRowContainer } from './styles';
import { trackGA4FiltersDiffThunk } from '../../reducers/current-filter-states';

interface TagItem {
    label: string;
    value: any;
    name: string;
}

export function skipToSrpBySearchFilter(filters: Record<string, any>) {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.pathname = url.pathname.replace(/\/page[1-9]$/i, '');
    Object.keys(filters).forEach((key) => {
        const value = filters[key];
        let searchValue;
        if (Array.isArray(value)) {
            searchValue = value.join(',');
        } else {
            searchValue = value;
        }
        isEmpty(searchValue)
            ? url.searchParams.delete(key)
            : url.searchParams.set(key, searchValue);
    });

    window.location.href = url.toString();
}

const MultiFilters = ({
    ['data-q']: dataQ,
    className,
}: {
    ['data-q']: string;
    className?: string;
}) => {
    const {
        resultsPage: { filtersDictionary, currentFilterStates, multiFiltersOrder },
    }: SrpState = useSelector((data: SrpState): SrpState => data);
    const dispatch = useDispatch() as SrpUseDispatch;
    const filters: MultiSelectGroupProps['filters'] = multiFiltersOrder.map((i) => ({
        name: i,
        label: filtersDictionary[i].label as string,
        options:
            i === 'price'
                ? undefined
                : filtersDictionary[i].values.map((value) => ({
                      label: value.label,
                      value: value.value,
                  })),
        type: i === 'price' ? 'price' : 'multi-select',
    }));
    const showClearAll = multiFiltersOrder.some((i) => !isEmpty(currentFilterStates[i]));
    const priceValue = { min: currentFilterStates.min_price, max: currentFilterStates.max_price };
    const onChange = (_value, changed: { key: string; value: any }) => {
        // const price = value?.price ?? {};

        if (changed?.key === 'price') {
            let updatedSearchFilters: Record<string, string> = {};

            if (changed?.value?.min !== currentFilterStates.min_price) {
                updatedSearchFilters.min_price = changed?.value?.min;
            }
            if (changed?.value?.max !== currentFilterStates.max_price) {
                updatedSearchFilters.max_price = changed?.value?.max;
            }

            dispatch(
                trackGA4FiltersDiffThunk({
                    searchFilters: {
                        min_price: changed?.value?.min,
                        max_price: changed?.value?.max,
                    },
                    updatedSearchFilters,
                })
            );
            skipToSrpBySearchFilter({
                min_price: changed?.value?.min,
                max_price: changed?.value?.max,
            });
        } else {
            dispatch(
                trackGA4FiltersDiffThunk({
                    searchFilters: {
                        [changed?.key]: changed?.value,
                    },
                    updatedSearchFilters:
                        changed.key &&
                        JSON.stringify(currentFilterStates[changed.key]) !==
                            JSON.stringify(changed.value)
                            ? {
                                  [changed?.key]: changed?.value,
                              }
                            : {},
                })
            );
            skipToSrpBySearchFilter({ [changed?.key]: changed?.value });
        }
    };
    useEffect(() => {
        multiFiltersOrder.map((i) => {
            trackGA4Event<GA4.SearchFilterEvent>({
                event: 'impress_search_filter',
                filterName: i,
            });
        });
    }, [multiFiltersOrder]);
    const tags: TagItem[] = useMemo(() => {
        const tags: { name: string; label: string; value: any }[] = [];
        filters.forEach((filter) => {
            if (filter.type === 'price') {
                currentFilterStates.min_price &&
                    tags.push({
                        name: 'min_price',
                        label: `From £${currentFilterStates.min_price}`,
                        value: currentFilterStates.min_price,
                    });
                currentFilterStates.max_price &&
                    tags.push({
                        name: 'max_price',
                        label: `To £${currentFilterStates.max_price}`,
                        value: currentFilterStates.max_price,
                    });
            } else {
                const values = currentFilterStates[filter.name];
                if (values && Array.isArray(values)) {
                    values.forEach((i) => {
                        tags.push({
                            name: filter.name,
                            label: filter.options?.find((j) => j.value === i)?.label || '',
                            value: i,
                        });
                    });
                }
            }
        });
        return tags;
    }, [currentFilterStates, filters]);

    const onClose = (tag: TagItem, _index?: number) => {
        const { name, value } = tag;
        if (['min_price', 'max_price'].includes(name)) {
            currentFilterStates[name] = undefined;
            if ((currentFilterStates as any).price) {
                (currentFilterStates as any).price[name] = undefined;
            }
        } else {
            const index = currentFilterStates[name].indexOf(value);
            currentFilterStates[name].splice(index, 1);
        }
        onChange(null, { key: name, value: currentFilterStates[name] });
    };
    const clearAll = () => {
        const filtersKeys = filters
            .map((i) => (i.name === 'price' ? ['max_price', 'min_price'] : i.name))
            .flat();
        trackGA4PreNav<GA4.FilterButtonClickEvent>({
            event: 'filter_button_click',
            filterName: 'clear_all',
            linkText: 'Clear All',
        });
        skipToSrpBySearchFilter(
            filtersKeys.reduce((acc, key) => ({ ...acc, [key]: undefined }), {})
        );
    };
    const clearEvent = (filter) => {
        trackGA4PreNav<GA4.FilterButtonClickEvent>({
            event: 'filter_button_click',
            filterName: filter.name,
            linkText: 'Clear All',
        });
        onChange(null, { key: filter.name, value: undefined });
    };
    if (filters.length === 0) return null;
    return (
        <FilterRowContainer className={className}>
            <FilterContainer data-q={dataQ}>
                <MultiSelectGroup
                    css={cssStyle`
                    flex: 1;
                `}
                    filters={filters}
                    value={{ ...currentFilterStates, price: priceValue }}
                    onChange={onChange}
                    onClear={clearEvent}
                />
                {showClearAll && <ClearAll onClick={clearAll}>Clear All</ClearAll>}
            </FilterContainer>
            {!!tags.length && (
                <TagList>
                    {tags.map(
                        (i) =>
                            i.label && (
                                <StyledTag
                                    key={i.label}
                                    onClose={() => onClose(i)}
                                    iconColor="blade"
                                    label={i.label}
                                />
                            )
                    )}
                </TagList>
            )}
        </FilterRowContainer>
    );
};

export default MultiFilters;
